import {
    SegmentedCarousel,
    ToggleChipProps,
    useSegmentedCarouselController,
} from '@eventbrite/collection-carousel';
import {
    BudgetEventsExperiment,
    Location,
    useGetStatsigEnrollment,
} from '@eventbrite/discover-utils';
import { EmptyState } from '@eventbrite/eds-empty-state';
import { GhostGraphic } from '@eventbrite/eds-iconography';
import { ShareUTMOptions } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { getCurrencySymbol } from '@eventbrite/intl';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { Typography } from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import React, { useEffect, useMemo, useState } from 'react';
import DiscoverVerticalEventCard from '../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import { EventLoadingState } from '../../pages/categoryBrowse/components/CategoryBrowseEventsLoading';
import {
    HOMEPAGE_CLICK_BUDGET_CAROUSEL_TAB,
    HOMEPAGE_CLICK_BUDGET_EVENT_CAROUSEL,
    HOMEPAGE_VIEW_BUDGET_EVENT_CAROUSEL,
} from '../../pages/home/constants/analytics';
import { useCategoryEventsQuery } from '../../pages/home/hooks/events/events';
import { countryMapping } from '../../__fixtures__/countryNameCountryCode';
import styles from './BudgetEventsCarousel.module.scss';

interface BudgetEventsCarouselProps {
    isAuthenticated: boolean;
    location: Location;
    locale: string;
}

interface Tab {
    id: string;
    label: GenericLazyString;
    min: number;
    max: number;
}

const PAGE_SIZE = 10;

const utmOptions: ShareUTMOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-share-source': 'home-page',
    'utm-term': '',
};

const renderLoadingSkeleton = () => (
    <div className="loader" data-testid="loading-skeleton">
        <LoadingSkeleton height="540px" />
    </div>
);

const renderEventLoadingState = () => (
    <div className="eventLoader">
        <EventLoadingState numberOfEvents={5} />
    </div>
);

export const BudgetEventsCarousel: React.FC<BudgetEventsCarouselProps> = ({
    isAuthenticated,
    location,
    locale,
}) => {
    const budgetEventsExperiment = useGetStatsigEnrollment({
        name: BudgetEventsExperiment.name,
        paramName: BudgetEventsExperiment.paramName,
        defaultValue: BudgetEventsExperiment.values.control,
    });

    const [currency, setCurrency] = useState({
        name: 'USD',
        symbol: '$',
    });

    const tabs: Tab[] = useMemo(
        () => [
            {
                id: 'under30',
                label: gettext('Under %(currencySymbol)s30', {
                    currencySymbol: currency.symbol,
                }),
                min: 0,
                max: 2900,
            },
            {
                id: 'between30and50',
                label: gettext(
                    'From %(currencySymbol)s30-%(currencySymbol)s50',
                    {
                        currencySymbol: currency.symbol,
                    },
                ),
                min: 3000,
                max: 5000,
            },
            {
                id: 'above50',
                label: gettext('Above %(currencySymbol)s50', {
                    currencySymbol: currency.symbol,
                }),
                min: 5100,
                max: 9999999,
            },
        ],
        [currency.symbol],
    );

    const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);

    const [initialRender, setInitialRender] = useState(true);

    useEffect(() => {
        if (location.country && countryMapping[location.country]) {
            const currencyName = countryMapping[location.country].currency;
            const symbol = getCurrencySymbol(currencyName);
            setInitialRender(true);
            setCurrency({ name: currencyName, symbol });
            setActiveTab(tabs[0]);
        }
    }, [location, tabs]);

    const { data, isFetching, isLoading } = useCategoryEventsQuery({
        queryOverload: {
            price: 'paid',
            price_range: {
                minimum_price: activeTab.min,
                maximum_price: activeTab.max,
                currency: currency.name,
            },
            page_size: PAGE_SIZE,
            aggs: {},
            dates: ['current_future'],
        },
        refetchOnMount: false,
        enabled: true,
        location,
    });

    const events = data?.events || [];
    const segmentedCarouselControllerProps = useSegmentedCarouselController();

    const handleSelectedTab = (selectedTab: ToggleChipProps) => {
        const foundTab = tabs.find((tab) => tab.id === selectedTab.id);
        if (foundTab) {
            setActiveTab(foundTab);
            logEvent(HOMEPAGE_CLICK_BUDGET_CAROUSEL_TAB, selectedTab.id);
        } else {
            console.error('Tab not found:', selectedTab.id);
        }
    };

    if (
        budgetEventsExperiment.status === 'loading' ||
        (isFetching && initialRender)
    ) {
        return renderLoadingSkeleton();
    }

    if (!isFetching && initialRender) {
        setInitialRender(false);
    }

    if (
        budgetEventsExperiment.variant ===
            BudgetEventsExperiment.values.control ||
        (!isFetching && events.length === 0 && activeTab.id === tabs[0].id)
    ) {
        return null;
    }

    return (
        <section
            data-testid="budget-events-carousel"
            className={styles.budgetEventsCarousel}
            data-heap-id={HOMEPAGE_VIEW_BUDGET_EVENT_CAROUSEL}
        >
            <SegmentedCarousel
                {...{
                    ...segmentedCarouselControllerProps,
                    rootPaddingTop: 0,
                    rootPaddingBottom: 0,
                    rootPaddingLeft: 0,
                    rootPaddingRight: 0,
                    headerGap: 0,
                }}
            >
                <SegmentedCarousel.Header>
                    <Typography color="neutral-900" variant="heading-sm">
                        {gettext('Events on a budget')}
                    </Typography>
                    <SegmentedCarousel.ToggleChips
                        header=""
                        toggleChips={tabs.map((tab) => ({
                            label: tab.label,
                            id: tab.id,
                            isSelected: tab.id === activeTab.id,
                        }))}
                        onSelected={handleSelectedTab}
                    />
                </SegmentedCarousel.Header>
                {/* Workaround: Force carousel update by including events length and isLoading in the key */}
                <SegmentedCarousel.Track
                    key={`budget-events-${activeTab.id}-isLoading-${isLoading}`}
                >
                    {isLoading && renderEventLoadingState()}
                    {!isLoading &&
                        events.map((event) => (
                            <DiscoverVerticalEventCard
                                key={event.id}
                                locale={locale}
                                isLazyImage
                                isAuthenticated={isAuthenticated}
                                event={event}
                                affCode="ehometext"
                                statsigLocationString={
                                    HOMEPAGE_CLICK_BUDGET_EVENT_CAROUSEL
                                }
                                data-testid={`homepage-event-card-${event.id}`}
                                shareOptions={{
                                    utmOptions,
                                    trackingCategory: 'eventcard',
                                }}
                            />
                        ))}
                    {!isLoading && events.length === 0 && (
                        <EmptyState
                            graphicType={<GhostGraphic />}
                            title={gettext("We couldn't find anything")}
                            description={gettext(
                                'Adjust your filters and try again',
                            )}
                        />
                    )}
                </SegmentedCarousel.Track>
                <SegmentedCarousel.Footer />
            </SegmentedCarousel>
        </section>
    );
};
